import { Box, Stack, useToast } from '@chakra-ui/core'
import React, { forwardRef, useImperativeHandle } from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { FormRef } from 'redux/actions/campaigns'
import { IPresetPrompt, IPrompt } from 'redux/reducers/promts'
import { TitleField } from './TitleField'
import { ContentField } from './ContentField'
import { PromptField } from './PromptField'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { createPrompt, PROMPT_TYPES, updatePrompt } from 'redux/actions/promts'

const defaultValues: IPrompt = {
  data: {
    title: '',
    content: '',
    prompt_type: PROMPT_TYPES.PRESET,
  },
  prompt: '',
  users: [],
  groups: [],
  tags: [],
}

interface PromptFormProps {
  onClose: () => void
  prompt?: (IPresetPrompt & { prompt: string }) | null
}

const serializData = (data: IPresetPrompt & { prompt: string }): IPrompt => {
  return {
    data: {
      title: data.title,
      content: data.content,
      prompt_type: PROMPT_TYPES.PRESET,
    },
    prompt: data.prompt,
    users: [],
    groups: [],
    tags: [],
  }
}

export const PromptForm = forwardRef<FormRef, PromptFormProps>(({ onClose, prompt }, ref) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const isUpdate = !!prompt

  const methods = useForm<IPrompt>({
    defaultValues: prompt ? serializData(prompt) : defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const onSubmit = async (data: IPrompt) => {
    const copiedData = { ...data }

    if (!isUpdate) {
      copiedData.data.prompt_type = PROMPT_TYPES.PRESET
    }

    try {
      const res = isUpdate
        ? await dispatch(
            updatePrompt({
              ...copiedData,
              prompt_id: prompt?.oid,
            })
          )
        : await dispatch(createPrompt(copiedData))
      if (res) {
        onClose()
        toast({
          title: isUpdate ? 'Prompt updated successfully' : 'Prompt created successfully',
          status: 'success',
          position: 'top-right',
        })
      }
      onClose()
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: isUpdate ? 'Failed to update prompt' : 'Failed to create prompt',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  useImperativeHandle(ref, () => ({
    submit: async () => {
      return methods.handleSubmit(onSubmit)()
    },
    onClose,
  }))

  return (
    <FormContext {...methods}>
      <Stack direction="column" style={{ rowGap: '20px' }}>
        <TitleField />
        <ContentField />
        <PromptField />
      </Stack>
      <Controller name="users" control={methods.control} as={<Box display="none" />} />
      <Controller name="groups" control={methods.control} as={<Box display="none" />} />
      <Controller name="tags" control={methods.control} as={<Box display="none" />} />
    </FormContext>
  )
})
